/**
 * URL-backed filter state for the CRM lane views.
 *
 * The URL is the single source of truth for `q`, `lane`, `due`, and `page` —
 * modelled on `useThreadFilters` in `src/hooks/useConversationThreads.ts`.
 * Every filtered/paged view is therefore linkable, and back/forward
 * navigation restores it. The one exception is the search box itself: it
 * keeps local state and only pushes to the URL after `useDebounce` settles,
 * so the query (which reads only `filters.q`, never the local input) is not
 * re-run and history is not spammed on every keystroke.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import type { DashboardFilter, DashboardLane } from '@/lib/crm/dashboard';

export interface CrmFilters {
  q: string;
  lane: DashboardLane | null;
  due: DashboardFilter;
  page: number;
}

const DEFAULT_FILTERS: CrmFilters = {
  q: '',
  lane: null,
  due: null,
  page: 1,
};

const SEARCH_DEBOUNCE_MS = 300;

const VALID_LANES: readonly string[] = [
  'my-turn', 'their-turn', 'scheduled', 'nurture', 'on-hold',
];

const VALID_DUE_FILTERS: readonly string[] = [
  'overdue', 'due-today', 'due-this-week', 'total-active',
];

function parseLane(value: string | null): DashboardLane | null {
  return value && VALID_LANES.includes(value) ? (value as DashboardLane) : null;
}

function parseDue(value: string | null): DashboardFilter {
  return value && VALID_DUE_FILTERS.includes(value)
    ? (value as Exclude<DashboardFilter, null>)
    : null;
}

export interface UseCrmFiltersResult {
  filters: CrmFilters;
  setFilters: (updates: Partial<Omit<CrmFilters, 'q'>>) => void;
  /** Local, un-debounced value for controlling the search input. */
  searchInput: string;
  setSearchInput: (value: string) => void;
}

export function useCrmFilters(): UseCrmFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo((): CrmFilters => {
    const pageParam = Number.parseInt(searchParams.get('page') ?? '1', 10);
    const page = Number.isNaN(pageParam) || pageParam < 1 ? 1 : pageParam;

    return {
      q: searchParams.get('q') ?? DEFAULT_FILTERS.q,
      lane: parseLane(searchParams.get('lane')),
      due: parseDue(searchParams.get('due')),
      page,
    };
  }, [searchParams]);

  const [searchInput, setSearchInput] = useState<string>(filters.q);
  const [debouncedSearch] = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  // Writes the debounced value to the URL once it settles. The query layer
  // reads only `filters.q` (derived from the URL above), never
  // `searchInput` directly, so a filtered view stays linkable and typing
  // never spams history with one entry per keystroke.
  useEffect(() => {
    if (debouncedSearch === filters.q) return;

    const next = new URLSearchParams(searchParams);
    if (debouncedSearch) {
      next.set('q', debouncedSearch);
    } else {
      next.delete('q');
    }
    next.set('page', '1');
    setSearchParams(next, { replace: true });
    // Only the debounced value should re-trigger this write; `searchParams`/
    // `filters.q` change as a RESULT of this effect, so including them would
    // either fight the debounce or is redundant with the guard above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const setFilters = useCallback(
    (updates: Partial<Omit<CrmFilters, 'q'>>): void => {
      const next = new URLSearchParams(searchParams);
      let nonPageFilterChanged = false;

      if (updates.lane !== undefined) {
        if (updates.lane) {
          next.set('lane', updates.lane);
        } else {
          next.delete('lane');
        }
        nonPageFilterChanged = true;
      }

      if (updates.due !== undefined) {
        if (updates.due) {
          next.set('due', updates.due);
        } else {
          next.delete('due');
        }
        nonPageFilterChanged = true;
      }

      if (updates.page !== undefined) {
        next.set('page', String(updates.page));
      }

      // Reset to page 1 whenever a non-page filter changes, so a stale page
      // number from a previous, differently-scoped result set is never
      // silently reused.
      if (nonPageFilterChanged) {
        next.set('page', '1');
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return { filters, setFilters, searchInput, setSearchInput };
}
